import React, { Suspense, useEffect } from 'react';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Helmet, HelmetProvider } from "react-helmet-async";
import Index from "./pages/Index"; // Keep Index loaded for homepage
import ScrollToTop from "./components/ScrollToTop";
import { CookieConsentProvider } from "./components/cookie-consent/CookieConsentProvider";
import CookieBanner from "./components/cookie-consent/CookieBanner";
import PageContainer from "./components/PageContainer";
import WordPressResourceDetector from "./components/diagnostics/WordPressResourceDetector";
const CityPage = React.lazy(() => import("./components/city/CityPage"));
import { initializeVersionCheck } from "./utils/versionService";

// Lazy load all route components except Index (homepage)
const Diensten = React.lazy(() => import("./pages/Diensten"));
const OverOns = React.lazy(() => import("./pages/OverOns"));
const Contact = React.lazy(() => import("./pages/Contact"));
const Calculator = React.lazy(() => import("./pages/Calculator"));
const NotFound = React.lazy(() => import("./pages/NotFound"));
const Blog = React.lazy(() => import("./pages/Blog"));
const BlogPost = React.lazy(() => import("./pages/BlogPost"));
const PrivacyPolicy = React.lazy(() => import("./pages/PrivacyPolicy"));
const CookiePolicy = React.lazy(() => import("./pages/CookiePolicy"));
const AlgemeneVoorwaarden = React.lazy(() => import("./pages/AlgemeneVoorwaarden"));
const TelemarketingScan = React.lazy(() => import("./pages/TelemarketingScan"));
const TelemarketingScanResults = React.lazy(() => import("./pages/TelemarketingScanResults"));
const LeadgeneratieKmo = React.lazy(() => import("./pages/LeadgeneratieKmo"));
const AILeadEngine = React.lazy(() => import("./pages/AILeadEngine"));
const InternalPitchNoCure = React.lazy(() => import("./pages/InternalPitchNoCure"));
const InternalAIDemoTest = React.lazy(() => import("./pages/InternalAIDemoTest"));
const CompanyPitch = React.lazy(() => import("./pages/CompanyPitch"));
const Jobs = React.lazy(() => import("./pages/Jobs"));
const OldVsNewProspectie = React.lazy(() => import("./pages/internal/OldVsNewProspectie"));
const Bedankt = React.lazy(() => import("./pages/Bedankt"));
const Producten = React.lazy(() => import("./pages/Producten"));

const queryClient = new QueryClient();

// Site-wide default SEO/social tags. Rendered once at the root so every
// route's PageSEO/BlogSEO output cleanly overrides these via react-helmet-async's
// nested-Helmet merge (matched by tag name/property) instead of shipping a
// second, duplicate tag alongside a page-specific one.
const DefaultSEO = () => (
  <Helmet>
    <meta name="description" content="Afsprakenmaker.be, het toonaangevende B2B callcenter in Vlaanderen, vult jouw agenda met warme, relevante afspraken zodat jij kan doen waar je goed in bent: klanten helpen en je bedrijf laten groeien." />
    <meta name="author" content="Afsprakenmaker.be" />

    <meta property="og:type" content="website" />
    <meta property="og:url" content="https://afsprakenmaker.be/" />
    <meta property="og:title" content="Afsprakenmaker.be | #1 B2B Callcenter in Vlaanderen" />
    <meta property="og:description" content="Afsprakenmaker.be, het beste B2B callcenter in Vlaanderen, vult jouw agenda met warme, relevante afspraken zodat jij kan doen waar je goed in bent: klanten helpen en je bedrijf laten groeien." />
    <meta property="og:image" content="https://afsprakenmaker.be/lovable-uploads/ac401e9b-2809-41cc-9a40-cdd109a1f836.png" />
    <meta property="og:locale" content="nl_BE" />

    <meta name="twitter:card" content="summary_large_image" />
    <meta name="twitter:title" content="Afsprakenmaker.be | #1 B2B Callcenter in Vlaanderen" />
    <meta name="twitter:description" content="Het beste B2B callcenter in Vlaanderen voor telemarketing en afspraken maken" />
    <meta name="twitter:image" content="https://afsprakenmaker.be/lovable-uploads/ac401e9b-2809-41cc-9a40-cdd109a1f836.png" />

    <link rel="alternate" href="https://afsprakenmaker.be/" hrefLang="nl-BE" />
  </Helmet>
);

// Loading component for lazy-loaded routes
const RouteLoading = () => (
  <PageContainer>
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>
  </PageContainer>
);

// Higher-order component to wrap lazy routes with consistent loading
const LazyRoute = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<RouteLoading />}>
    {children}
  </Suspense>
);

const App = () => {
  console.log("App component rendering with all routes");
  
  // Initialize version checking on app mount
  useEffect(() => {
    initializeVersionCheck();
  }, []);
  
  // Determine if we should show the WordPress resource detector
  const isDevelopment = process.env.NODE_ENV === 'development';
  
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <HelmetProvider>
          <DefaultSEO />
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <CookieConsentProvider>
              
              <ScrollToTop />
              <Routes>
                <Route path="/" element={<PageContainer><Index /></PageContainer>} />
                <Route path="/diensten" element={<LazyRoute><PageContainer><Diensten /></PageContainer></LazyRoute>} />
                <Route path="/diensten/:citySlug" element={<LazyRoute><PageContainer><CityPage /></PageContainer></LazyRoute>} />
                <Route path="/producten" element={<LazyRoute><PageContainer><Producten /></PageContainer></LazyRoute>} />
                
                {/* Keep waarom route for backward compatibility but redirect to new page */}
                <Route path="/waarom" element={<LazyRoute><PageContainer><LeadgeneratieKmo /></PageContainer></LazyRoute>} />
                
                <Route path="/over-ons" element={<LazyRoute><PageContainer><OverOns /></PageContainer></LazyRoute>} />
                <Route path="/calculator" element={<LazyRoute><PageContainer><Calculator /></PageContainer></LazyRoute>} />
                
                {/* Jobs page */}
                <Route path="/jobs" element={<LazyRoute><PageContainer><Jobs /></PageContainer></LazyRoute>} />
                <Route path="/vacatures" element={<LazyRoute><PageContainer><Jobs /></PageContainer></LazyRoute>} />
                
                {/* AI Lead Engine */}
                <Route path="/ai-lead-engine" element={<LazyRoute><PageContainer withVerticalPadding={false}><AILeadEngine /></PageContainer></LazyRoute>} />
                
                {/* Keep old script generator route for backward compatibility */}
                <Route path="/script-generator" element={<LazyRoute><PageContainer withVerticalPadding={false}><AILeadEngine /></PageContainer></LazyRoute>} />
                
                {/* Internal Tools */}
                <Route path="/internal/pitch-nocure-beta" element={<LazyRoute><InternalPitchNoCure /></LazyRoute>} />
                <Route path="/internal/ai-demo-test" element={<LazyRoute><InternalAIDemoTest /></LazyRoute>} />
                <Route path="/internal/company-pitch" element={<LazyRoute><CompanyPitch /></LazyRoute>} />
                <Route path="/internal/old-vs-new-prospectie" element={<LazyRoute><OldVsNewProspectie /></LazyRoute>} />
                
                <Route path="/contact" element={<LazyRoute><PageContainer><Contact /></PageContainer></LazyRoute>} />
                
                <Route path="/blog" element={<LazyRoute><PageContainer><Blog /></PageContainer></LazyRoute>} />
                <Route path="/blog/:postId" element={<LazyRoute><PageContainer><BlogPost /></PageContainer></LazyRoute>} />
                <Route path="/privacybeleid" element={<LazyRoute><PageContainer><PrivacyPolicy /></PageContainer></LazyRoute>} />
                <Route path="/cookiebeleid" element={<LazyRoute><PageContainer><CookiePolicy /></PageContainer></LazyRoute>} />
                <Route path="/algemene-voorwaarden" element={<LazyRoute><PageContainer><AlgemeneVoorwaarden /></PageContainer></LazyRoute>} />
                <Route path="/telemarketing-scan" element={<LazyRoute><PageContainer><TelemarketingScan /></PageContainer></LazyRoute>} />
                <Route path="/telemarketing-scan/results" element={<LazyRoute><PageContainer><TelemarketingScanResults /></PageContainer></LazyRoute>} />
                
                <Route path="/leadgeneratie-kmo" element={<LazyRoute><PageContainer><LeadgeneratieKmo /></PageContainer></LazyRoute>} />
                <Route path="/bedankt" element={<LazyRoute><PageContainer><Bedankt /></PageContainer></LazyRoute>} />
                <Route path="*" element={<LazyRoute><PageContainer><NotFound /></PageContainer></LazyRoute>} />
              </Routes>
              <CookieBanner />
              {isDevelopment && <WordPressResourceDetector />}
            </CookieConsentProvider>
          </BrowserRouter>
        </HelmetProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
